import { FC, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { GroupBase, OptionProps, components } from 'react-select';

import {
  ActionBtnStyled,
  ControlsWrapStyled,
  FlexContainerMessageStyled,
  FormElementWrapStyled,
  FormStyled,
  TypographyTitleStyled,
  getPopupContentStyle,
} from './ChangeTicketPriorityPopup.styled';
import useChangePriorityPopup from './hooks/useChangeTicketPriorityPopup';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import { FormSelect } from 'components/Form';
import Popup from 'components/Popup/Popup';
import PriorityIcon from 'components/PriorityIcon/PriorityIcon';
import SuccessSection from 'components/SuccessSection/SuccessSection';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { NoopT } from 'declarations/types/common';
import { SelectOptionT } from 'declarations/types/select';

type ChangeTicketPriorityPopupPropsT = {
  ticketId: string;
  ticketPriorityId: number;
  isOpen: boolean;
  onClose: NoopT;
};

export type ChangeTicketPriorityFormValuesT = {
  ticketId: string;
  ticketPriorityId: number;
};

const ChangeTicketPriorityPopup: FC<ChangeTicketPriorityPopupPropsT> = ({
  ticketId,
  ticketPriorityId,
  isOpen,
  onClose,
}) => {
  const { isMobile } = useResponsive();
  const {
    isChangingPriority,
    isPriorityChanged,
    isPriorityChangeError,
    ticketPrioritiesOptions,
    isLoadingPriorityesOptions,
    changeTicketPriorityErrorMessage,
    changeTicketPriorityValidationErrorMessage,
    showPopupContent,
    handleChangeTicketPriority,
    handleClosePopup,
  } = useChangePriorityPopup();

  const fromMethods = useForm<ChangeTicketPriorityFormValuesT>({
    defaultValues: {
      ticketId: ticketId,
      ticketPriorityId: ticketPriorityId,
    },
    mode: 'onChange',
  });

  const { control, handleSubmit, setValue } = fromMethods;

  useEffect(() => {
    if (isOpen) {
      setValue('ticketId', ticketId);
      setValue('ticketPriorityId', ticketPriorityId);
    }
  }, [isOpen, ticketId, ticketPriorityId, setValue]);

  const selectedPriorityId = useWatch<ChangeTicketPriorityFormValuesT, 'ticketPriorityId'>({
    control,
    name: 'ticketPriorityId',
  });

  const { Option } = components;
  const OptionWithPriorityIcon = (
    props: JSX.IntrinsicAttributes & OptionProps<SelectOptionT, false, GroupBase<SelectOptionT>>
  ) => (
    <Option {...props}>
      <PriorityIcon priority={(props.data as SelectOptionT).label} />
      {(props.data as SelectOptionT).label}
    </Option>
  );

  return (
    <Popup
      nested
      isOpen={isOpen}
      showCloseBtn={isPriorityChanged || isPriorityChangeError}
      popupId={ScrollLockIdE.ChangeTicketPriorityPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      onClose={handleClosePopup(onClose)}
      closeOnEscape={false}
    >
      {showPopupContent && (
        <>
          <TypographyTitleStyled variant="h2" color="dark" align="center">
            Change priority
          </TypographyTitleStyled>
          <FormProvider {...fromMethods}>
            <FormStyled role="form" onSubmit={handleSubmit(handleChangeTicketPriority)}>
              <FormElementWrapStyled>
                <FormSelect
                  isRequired
                  label="Choose ticket priority"
                  name="ticketPriorityId"
                  placeholder="Ticket priorities"
                  options={ticketPrioritiesOptions}
                  isLoading={isLoadingPriorityesOptions}
                  components={{ Option: OptionWithPriorityIcon }}
                />
              </FormElementWrapStyled>
              <ControlsWrapStyled justify={isMobile ? 'center' : 'flex-end'}>
                <ActionBtnStyled
                  type="button"
                  variant="outline"
                  size="xl"
                  onClick={handleClosePopup(onClose)}
                >
                  Cancel
                </ActionBtnStyled>
                <ActionBtnStyled
                  variant="contained"
                  size="xl"
                  isLoading={isChangingPriority}
                  disabled={selectedPriorityId === ticketPriorityId}
                >
                  Save
                </ActionBtnStyled>
              </ControlsWrapStyled>
            </FormStyled>
          </FormProvider>
        </>
      )}

      {isPriorityChanged && (
        <FlexContainerMessageStyled direction="column">
          <SuccessSection message="Priority was changed successfully" />
        </FlexContainerMessageStyled>
      )}

      {isPriorityChangeError && (
        <FlexContainerMessageStyled direction="column">
          <ErrorSection
            errorDetails={
              changeTicketPriorityErrorMessage || changeTicketPriorityValidationErrorMessage
            }
          />
        </FlexContainerMessageStyled>
      )}
    </Popup>
  );
};

export default ChangeTicketPriorityPopup;
